import React, { useEffect, useState } from 'react';
import { sitesService, userService } from '../services/api';
import { useDocumentTypes } from '../context/DocumentTypesContext';
import { HiSearch, HiTrash } from 'react-icons/hi';
import {
  HEADER_RULE,
  ROW_RULE,
  STICKY_LEFT_EDGE,
  TABLE,
  TABLE_STYLE,
  TH,
  Z_BODY_PINNED,
  Z_HEADER,
  Z_HEADER_PINNED,
} from '../theme/table';

const PAGE_SIZE = 10;

const SitesPage = () => {
  // Permission columns follow the document_types catalog, so a new type is
  // immediately assignable instead of needing this list edited.
  const { types: docTypes } = useDocumentTypes();
  const ALL_DOC_TYPES = docTypes.map((t) => ({ value: t.key, label: t.label }));
  const [sites, setSites] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedSite, setSelectedSite] = useState(null);
  const [siteUsers, setSiteUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null); // user_id being saved
  const [toast, setToast] = useState(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newSite, setNewSite] = useState({ name: '', slug: '' });
  const [creating, setCreating] = useState(false);

  const showToast = (type, msg) => {
    setToast({ type, msg });
    setTimeout(() => setToast(null), 3000);
  };

  useEffect(() => {
    Promise.all([sitesService.list(), userService.list()])
      .then(([sitesData, usersData]) => {
        setSites(sitesData);
        setUsers(usersData);
        if (sitesData.length > 0) setSelectedSite(sitesData[0]);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!selectedSite) return;
    setPage(1);
    sitesService.getSiteUsers(selectedSite.id).then(setSiteUsers);
  }, [selectedSite]);

  useEffect(() => {
    setPage(1);
  }, [search]);

  const filteredUsers = users.filter(
    (u) =>
      !u.is_superuser &&
      (search === '' ||
        (u.full_name || '').toLowerCase().includes(search.toLowerCase()) ||
        u.email.toLowerCase().includes(search.toLowerCase()))
  );
  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / PAGE_SIZE));
  const pagedUsers = filteredUsers.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const getPermission = (userId) => siteUsers.find((p) => p.user_id === userId);

  const handleToggleType = async (user, docType) => {
    const current = getPermission(user.id);
    const currentTypes = current?.allowed_document_types ?? [];
    const newTypes = currentTypes.includes(docType)
      ? currentTypes.filter((t) => t !== docType)
      : [...currentTypes, docType];

    setSaving(user.id);
    try {
      const updated = await sitesService.setUserPermission(selectedSite.id, user.id, newTypes);
      setSiteUsers((prev) => {
        const exists = prev.find((p) => p.user_id === user.id);
        if (exists) return prev.map((p) => (p.user_id === user.id ? updated : p));
        return [...prev, updated];
      });
    } catch {
      showToast('error', 'Failed to update permission');
    } finally {
      setSaving(null);
    }
  };

  const handleRemoveUser = async (userId) => {
    setSaving(userId);
    try {
      await sitesService.removeUserPermission(selectedSite.id, userId);
      setSiteUsers((prev) => prev.filter((p) => p.user_id !== userId));
    } catch {
      showToast('error', 'Failed to remove permission');
    } finally {
      setSaving(null);
    }
  };

  const handleCreateSite = async (e) => {
    e.preventDefault();
    if (!newSite.name.trim() || !newSite.slug.trim()) return;
    setCreating(true);
    try {
      const created = await sitesService.createSite({ name: newSite.name.trim(), slug: newSite.slug.trim() });
      setSites((prev) => [...prev, created]);
      setSelectedSite(created);
      setShowCreateModal(false);
      setNewSite({ name: '', slug: '' });
      showToast('success', `Site "${created.name}" created`);
    } catch {
      showToast('error', 'Failed to create site');
    } finally {
      setCreating(false);
    }
  };

  if (loading) {
    return <div className="page-container"><p className="text-slate-500">Loading...</p></div>;
  }

  return (
    <div className="page-container max-w-5xl h-full flex flex-col">
      <h2 className="text-2xl font-bold text-slate-800 mb-1">Site permissions</h2>
      <p className="text-slate-500 mb-6">Manage which document types each user can access per site.</p>

      {toast && (
        <div className={`mb-4 px-4 py-3 rounded-lg text-sm font-medium ${
          toast.type === 'error' ? 'bg-red-50 text-red-700' : 'bg-emerald-50 text-emerald-700'
        }`}>
          {toast.msg}
        </div>
      )}

      {/* Site tabs + Add button */}
      <div className="flex items-end gap-2 mb-6 border-b border-slate-200 pb-0">
        {sites.map((site) => (
          <button
            key={site.id}
            onClick={() => setSelectedSite(site)}
            className={`px-5 py-2.5 text-sm font-medium rounded-t-lg border-b-2 transition-all ${
              selectedSite?.id === site.id
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            {site.name}
          </button>
        ))}
        <button
          onClick={() => setShowCreateModal(true)}
          className="ml-auto mb-1 px-3 py-1.5 text-xs font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          + Add Site
        </button>
      </div>

      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6">
            <h3 className="text-lg font-semibold text-slate-800 mb-4">Create New Site</h3>
            <form onSubmit={handleCreateSite} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Site Name</label>
                <input
                  type="text"
                  value={newSite.name}
                  onChange={(e) => setNewSite((s) => ({ ...s, name: e.target.value }))}
                  placeholder="e.g. VTF Branch"
                  required
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Slug</label>
                <input
                  type="text"
                  value={newSite.slug}
                  onChange={(e) => setNewSite((s) => ({ ...s, slug: e.target.value.toLowerCase().replace(/\s+/g, '_') }))}
                  placeholder="e.g. vtf_branch"
                  required
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-slate-400 mt-1">Lowercase letters, numbers, and underscores only.</p>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => { setShowCreateModal(false); setNewSite({ name: '', slug: '' }); }}
                  className="px-4 py-2 text-sm rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={creating}
                  className="px-4 py-2 text-sm rounded-lg bg-blue-600 text-white font-medium hover:bg-blue-700 disabled:opacity-50"
                >
                  {creating ? 'Creating...' : 'Create Site'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {selectedSite && (
        <div className="card p-0 overflow-hidden flex-1 min-h-[16rem] flex flex-col">
          <div className="p-4 border-b border-slate-100">
            <div className="relative max-w-sm">
              <HiSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" aria-hidden="true" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name or email…"
                className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* The document-type columns grow with the catalog, so this table
              widens as admins add types — the user column stays pinned so a row
              never loses the name it belongs to. */}
          <div className="flex-1 overflow-auto">
          <table className={`${TABLE} text-sm`} style={TABLE_STYLE}>
            <thead>
              <tr>
                <th
                  className={`${TH} ${HEADER_RULE} sticky left-0 top-0 ${Z_HEADER_PINNED} bg-white ${STICKY_LEFT_EDGE}`}
                  style={{ minWidth: 220 }}
                >
                  User
                </th>
                {ALL_DOC_TYPES.map((dt) => (
                  <th key={dt.value} className={`${TH} ${HEADER_RULE} sticky top-0 ${Z_HEADER} bg-white text-center`}>
                    {dt.label}
                  </th>
                ))}
                <th className={`${TH} ${HEADER_RULE} sticky top-0 ${Z_HEADER} bg-white w-20`}></th>
              </tr>
            </thead>
            <tbody>
              {pagedUsers.map((user) => {
                const perm = getPermission(user.id);
                const allowed = perm?.allowed_document_types ?? [];
                const isSaving = saving === user.id;

                return (
                  <tr key={user.id} className="group hover:bg-slate-50 transition-colors">
                    <td
                      className={`py-3 px-4 ${ROW_RULE} sticky left-0 ${Z_BODY_PINNED} bg-white group-hover:bg-slate-50 ${STICKY_LEFT_EDGE}`}
                      style={{ minWidth: 220 }}
                    >
                      <div className="font-medium text-slate-800 truncate">{user.full_name || user.email}</div>
                      <div className="text-xs text-slate-400 truncate">{user.email}</div>
                    </td>
                    {ALL_DOC_TYPES.map((dt) => (
                      <td key={dt.value} className={`text-center py-3 px-3 ${ROW_RULE}`}>
                        <input
                          type="checkbox"
                          checked={allowed.includes(dt.value)}
                          disabled={isSaving}
                          onChange={() => handleToggleType(user, dt.value)}
                          className="w-4 h-4 accent-blue-600 cursor-pointer disabled:opacity-50"
                        />
                      </td>
                    ))}
                    <td className={`py-3 px-4 text-center ${ROW_RULE}`}>
                      {perm && (
                        <button
                          onClick={() => handleRemoveUser(user.id)}
                          disabled={isSaving}
                          title="Remove all permissions for this user on this site"
                          className="p-2 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 disabled:opacity-40"
                        >
                          <HiTrash className="w-4 h-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
              {pagedUsers.length === 0 && (
                <tr>
                  <td colSpan={ALL_DOC_TYPES.length + 2} className="py-8 text-center text-slate-400">
                    {search ? 'No users match your search.' : 'No non-superuser accounts found.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100">
            <p className="text-xs text-slate-400">
              {filteredUsers.length === 0
                ? 'No users'
                : `Showing ${(page - 1) * PAGE_SIZE + 1}–${Math.min(page * PAGE_SIZE, filteredUsers.length)} of ${filteredUsers.length} users`}
            </p>
            <div className="flex gap-1">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-3 py-1 text-xs rounded border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                <button
                  key={p}
                  onClick={() => setPage(p)}
                  className={`px-3 py-1 text-xs rounded border ${
                    p === page
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'border-slate-200 text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  {p}
                </button>
              ))}
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="px-3 py-1 text-xs rounded border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>

          <p className="text-xs text-slate-400 px-4 pb-3">
            Superusers have access to all sites and document types automatically.
          </p>
        </div>
      )}
    </div>
  );
};

export default SitesPage;
